import { getCollidingPairs, getVehiclePos, type Point2D } from './collision'
import { EPSILON, VELOCITY_EPSILON } from './constants'
import {
  clearRecentUncoupledCollisionKey,
  getRecentUncoupledPairIds,
  getTouchingSide,
  isRecentUncoupledCollision,
  runCouplingPass,
  syncRecentUncoupledCollisionKey,
} from './coupling'
import { computeUncoupleStatus } from './inputs'
import {
  applyMoves,
  buildConsistMoves,
  buildNPCConsistMoves,
  getMovementScale,
  loadProbe,
  moveVehicleBy,
} from './movement'
import {
  fillAllVehicles,
  getVehicleById,
  isLocomotiveMoving,
  rebuildOccupiedCells,
} from './queries'
import type { Network, Vehicle } from './types'
export { moveVehicleBy } from './movement'

const liveVehiclesScratch: Vehicle[] = []
const projectedVehiclesScratch: Vehicle[] = []
const projectedByIdScratch = new Map<string, Vehicle>()
const baseCollisionsScratch = new Set<string>()
const projectedCollisionsScratch = new Set<string>()
const locoMovesScratch = new Map<Vehicle, number>()
const npcMovesScratch = new Map<Vehicle, number>()
const moveQueueScratch: Vehicle[] = []
const distPosA: Point2D = { x: 0, z: 0 }
const distPosB: Point2D = { x: 0, z: 0 }

function distanceSq(network: Network, a: Vehicle, b: Vehicle): number | null {
  const pA = getVehiclePos(network, a, undefined, distPosA)
  const pB = getVehiclePos(network, b, undefined, distPosB)
  if (!pA || !pB) return null
  return (pA.x - pB.x) ** 2 + (pA.z - pB.z) ** 2
}

/**
 * The pair just uncoupled is still in contact but must not re-couple, so it behaves as a
 * solid buffer: any move that closes the gap would push one vehicle through the other.
 */
function closesRecentUncoupledGap(network: Network, projectedById: Map<string, Vehicle>): boolean {
  const ids = getRecentUncoupledPairIds(network)
  if (!ids) return false

  const realA = getVehicleById(network, ids[0])
  const realB = getVehicleById(network, ids[1])
  const projA = projectedById.get(ids[0])
  const projB = projectedById.get(ids[1])
  if (!realA || !realB || !projA || !projB) return false

  const before = distanceSq(network, realA, realB)
  const after = distanceSq(network, projA, projB)
  return before !== null && after !== null && after < before - EPSILON
}

function hasMeaningfulConstraint(targetDistance: number, moveScale: number): boolean {
  const blockedDistance = Math.abs(targetDistance) * Math.max(0, 1 - moveScale)
  return blockedDistance > EPSILON
}

function projectVehicles(
  network: Network,
  vehicles: Vehicle[],
  moves: Map<Vehicle, number>,
  scale: number,
): Vehicle[] {
  const projectedVehicles = projectedVehiclesScratch
  projectedVehicles.length = vehicles.length

  for (let i = 0; i < vehicles.length; i++) {
    const vehicle = vehicles[i]
    const projected = loadProbe(vehicle, projectedVehicles[i] ?? ({} as Vehicle))
    projected.linkedFront = vehicle.linkedFront
    projected.linkedBack = vehicle.linkedBack
    projectedVehicles[i] = projected

    const plannedMove = moves.get(vehicle)
    if (plannedMove && Math.abs(plannedMove) > EPSILON) {
      moveVehicleBy(network, projected, plannedMove * scale)
    }
  }

  return projectedVehicles
}

function areLinked(a: Vehicle | undefined, b: Vehicle | undefined): boolean {
  return (
    !!a &&
    !!b &&
    (a.linkedFront === b.id ||
      a.linkedBack === b.id ||
      b.linkedFront === a.id ||
      b.linkedBack === a.id)
  )
}

function introducesNewOverlap(
  network: Network,
  vehicles: Vehicle[],
  moves: Map<Vehicle, number>,
  baseCollisions: Set<string>,
  scale: number,
): boolean {
  syncRecentUncoupledCollisionKey(network)

  const projectedVehicles = projectVehicles(network, vehicles, moves, scale)
  const projectedCollisions = getCollidingPairs(
    network,
    projectedVehicles,
    projectedCollisionsScratch,
  )
  if (projectedCollisions.size === 0) return false

  const projectedById = projectedByIdScratch
  projectedById.clear()
  for (const v of projectedVehicles) projectedById.set(v.id, v)

  if (closesRecentUncoupledGap(network, projectedById)) return true

  for (const key of projectedCollisions) {
    if (baseCollisions.has(key)) continue

    const separator = key.indexOf('|')
    const vA = projectedById.get(key.slice(0, separator))
    const vB = projectedById.get(key.slice(separator + 1))
    if (!vA || !vB) continue

    const portA = getTouchingSide(network, vA, vB)
    const portB = getTouchingSide(network, vB, vA)
    if (isRecentUncoupledCollision(network, vA, portA, vB, portB)) return true
    if (!vA[portA] && !vB[portB]) continue
    if (areLinked(getVehicleById(network, vA.id), getVehicleById(network, vB.id))) continue

    return true
  }

  return false
}

function getSafeMovementScale(
  network: Network,
  moves: Map<Vehicle, number>,
  edgeConstraintScale: number,
): number {
  if (edgeConstraintScale <= EPSILON) return 0

  const vehicles = fillAllVehicles(network, liveVehiclesScratch)
  const baseCollisions = getCollidingPairs(network, vehicles, baseCollisionsScratch)
  if (introducesNewOverlap(network, vehicles, moves, baseCollisions, edgeConstraintScale)) return 0

  return edgeConstraintScale
}

function stepNPCs(network: Network, dt: number) {
  for (const npc of network.npcs.values()) {
    const isBraking = npc.speed === 0 || Math.sign(npc.speed) !== Math.sign(npc.currentVelocity)
    const accelRate = isBraking ? npc.deceleration : npc.acceleration
    const diff = npc.speed - npc.currentVelocity
    npc.currentVelocity =
      Math.abs(diff) <= accelRate * dt
        ? npc.speed
        : npc.currentVelocity + Math.sign(diff) * accelRate * dt

    if (Math.abs(npc.currentVelocity) < VELOCITY_EPSILON) continue

    const moves = buildNPCConsistMoves(
      network,
      npc,
      npc.currentVelocity * dt,
      npcMovesScratch,
      moveQueueScratch,
    )
    const scale = getMovementScale(network, moves)
    if (scale <= EPSILON) {
      npc.currentVelocity = 0
      npc.speed = 0
      continue
    }
    applyMoves(network, moves, scale)
  }
}

export function stepSimulation(network: Network, dt: number) {
  stepNPCs(network, dt)

  const loco = network.locomotive
  if (!loco || !isLocomotiveMoving(loco)) return

  const targetDs = loco.currentVelocity * dt
  if (!Number.isFinite(targetDs)) {
    loco.currentVelocity = 0
    return
  }

  const moves = buildConsistMoves(network, targetDs, locoMovesScratch, moveQueueScratch)

  const edgeScale = getMovementScale(network, moves)
  const moveScale = edgeScale <= EPSILON ? 0 : getSafeMovementScale(network, moves, edgeScale)
  const hasConstraint = hasMeaningfulConstraint(targetDs, moveScale)

  applyMoves(network, moves, moveScale)
  rebuildOccupiedCells(network)

  // If anything in the consist is blocked (e.g. bumper/dead-end), stop traction next frame.
  if (hasConstraint) {
    loco.currentVelocity = 0
    if (edgeScale <= EPSILON && !network.bumpBlocked) {
      network.bumpBlocked = targetDs < 0 ? 'backward' : 'forward'
      // Pinned between a dead end and a just-uncoupled wagon: drop the uncouple guard so
      // pushing back re-couples instead of leaving the loco unable to move either way.
      if (loco.uncoupleDirectionLock && loco.uncoupleDirectionLock !== network.bumpBlocked) {
        clearRecentUncoupledCollisionKey(network)
      }
    }
  } else if (edgeScale > EPSILON) {
    network.bumpBlocked = undefined
  }

  runCouplingPass(network, moves)
  computeUncoupleStatus(network)
}
